/**
 * The personal fundraising page: /p/<slug>.
 *
 * One person's appeal for one campaign. The page shows the owner (unless the
 * owner asked not to be named), the title, the goal and the progress - all of
 * it computed from the ledger by the API.
 *
 * It never shows a list of contributors. A page anybody may open, carrying
 * names, is personal data in the most exposed place there is; the count is the
 * fact and the names are not.
 */
import { apiFetch } from '@qalam/shared/frontend';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

export interface FundraiserPageData {
  slug: string;
  campaignSlug: string;
  titleAr: string;
  titleEn: string;
  descriptionAr: string;
  descriptionEn: string;
  goalCents: number;
  ownerName: string | null;
  raisedCents: number | null;
  seatsFunded: number;
  contributors: number;
  status: 'OPEN' | 'CLOSED';
}

export interface FundraiserPageProps {
  /** Injected so a test can serve its own page without a network. */
  load?: (slug: string) => Promise<FundraiserPageData | null>;
}

function defaultLoad(slug: string): Promise<FundraiserPageData | null> {
  return apiFetch<{ fundraiser: FundraiserPageData }>(`/api/public/fundraisers/${slug}`)
    .then((payload) => payload.fundraiser)
    .catch(() => null);
}

export default function FundraiserPage({ load = defaultLoad }: FundraiserPageProps) {
  const { t, i18n } = useTranslation();
  const { slug = '' } = useParams();
  const [page, setPage] = useState<FundraiserPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMissing(false);
    void load(slug)
      .then((found) => {
        if (cancelled) return;
        setPage(found);
        if (!found) setMissing(true);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, load]);

  if (loading) {
    return (
      <main data-testid="fundraiser-page">
        <p data-testid="fundraiser-loading">{t('fundraiser.loading')}</p>
      </main>
    );
  }

  if (missing || !page) {
    return (
      <main data-testid="fundraiser-page">
        <p data-testid="fundraiser-missing">{t('fundraiser.missing')}</p>
        <Link to="/">{t('nav.home')}</Link>
      </main>
    );
  }

  const arabic = i18n.language?.startsWith('ar');
  const title = arabic ? page.titleAr : page.titleEn;
  const description = arabic ? page.descriptionAr : page.descriptionEn;

  return (
    <main data-testid="fundraiser-page">
      <h1 data-testid="fundraiser-title">{title}</h1>
      {description ? <p data-testid="fundraiser-description">{description}</p> : null}
      <p data-testid="fundraiser-owner">{page.ownerName ?? t('fundraiser.anonymous')}</p>
      <section data-testid="fundraiser-progress">
        {page.raisedCents === null ? (
          <p data-testid="fundraiser-empty">{t('fundraiser.noMoneyYet')}</p>
        ) : (
          <p data-testid="fundraiser-raised">
            {t('fundraiser.raised', { amount: (page.raisedCents / 100).toFixed(2) })}
          </p>
        )}
        <p data-testid="fundraiser-goal">
          {t('fundraiser.goal', { amount: (page.goalCents / 100).toFixed(2) })}
        </p>
        <p data-testid="fundraiser-contributors">
          {t('fundraiser.contributors', { count: page.contributors })}
        </p>
      </section>
      <Link to={`/give?campaign=${page.campaignSlug}`} data-testid="fundraiser-give">
        {t('fundraiser.give')}
      </Link>
    </main>
  );
}
